import {
  createDictationEditorId,
  sectionDictateStatusAtom,
  sectionDictateStatusAtomFamily,
} from '@/atoms/dictationAtom';
import { windowIndexAtom } from '@/atoms/scribeTabsAtoms';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { DictationCleanupModes, LanguageCode } from '@heidi/schema/ml-scribe';
import { type Editor as TiptapEditor } from '@tiptap/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo } from 'react';

import { shouldStartDictationAtom } from '@/atoms/shouldStartDictation';
import { type DictationSource } from '@/components/constants';
import { IS_TAB_DICTATION_ENABLED } from '@/features/flags/legacy-flags';
import { useMutateUpdateSettings } from '@/hooks/mutation/useMutateUpdateSettings';
import { useQueryInputSupportedLanguages } from '@/hooks/query/useQuerySupportedLanguages';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useTranslations } from '@heidi/runtime/i18n';
import { useQuerySessionById } from '@heidi/session/data';

type UseDictateProps = {
  editor: TiptapEditor | null;
  source?: DictationSource;
  shouldShowCursor?: boolean;
  documentId?: string;
  onDictationSuccess?: () => void;
};

export const useDictate = ({
  editor,
  source,
  shouldShowCursor = false,
  documentId,
  onDictationSuccess,
}: UseDictateProps) => {
  const { t } = useTranslations();
  const isTabDictationEnabled = IS_TAB_DICTATION_ENABLED();
  const { data: supportedLanguages = [] } = useQueryInputSupportedLanguages();
  const { mutate: updateSessionSettings } = useMutateUpdateSettings({
    showToast: false,
  });
  const { data: userDictationPreference } = useQueryUser(
    (data) =>
      data?.session_settings?.default_dictation_cleanup_mode ??
      DictationCleanupModes.SMART
  );
  const isNonEnglishDictationEnabled = true;

  const sessionId = useCurrentSessionId();
  const windowIndex = useAtomValue(windowIndexAtom);

  const shouldQuerySession = !!sessionId;
  const { data: session } = useQuerySessionById({
    sessionId: shouldQuerySession ? sessionId : '', // Pass empty string when not querying
    select: shouldQuerySession
      ? (data) => {
          if (!data) return undefined;
          return {
            output_language_code: data.output_language_code,
            language_code_list: data.language_code_list,
            language_code: data.language_code,
          };
        }
      : undefined,
  });

  const editorId = useMemo(
    () => createDictationEditorId(windowIndex, source, documentId),
    [windowIndex, source, documentId]
  );
  const editorDictateAtom = useMemo(
    () => sectionDictateStatusAtomFamily(editorId),
    [editorId]
  );
  const [editorDictateStatus, setEditorDictateStatus] = useAtom(editorDictateAtom);
  const setLegacyDictateStatus = useSetAtom(sectionDictateStatusAtom);
  const setShouldStartDictation = useSetAtom(shouldStartDictationAtom);

  const isDictating = !!editorDictateStatus.isDictating;
  const isAskAiSource = source === 'askAi' || source === 'createTemplate';
  const isProcessing = !!editorDictateStatus.isProcessing;

  const startDictatingTooltipText = useMemo(() => {
    if (isAskAiSource) {
      return t('tooltips.dictate');
    }
    return userDictationPreference === DictationCleanupModes.SMART &&
      isTabDictationEnabled
      ? t('tooltips.startSmartDictation')
      : t('tabOptions.startWordForWord');
  }, [userDictationPreference, t, isAskAiSource, isTabDictationEnabled]);

  const isLanguageSupported = useMemo(() => {
    const outputLanguage = session?.output_language_code || LanguageCode.en;

    // note that deepgram only supports a single language code option
    // we use the output language only for dictation, ignore input languages
    const isLanguageDictationSupported = supportedLanguages.find(
      (language) =>
        // not all languages are supported by deepgram for dictation
        language.code === outputLanguage && language.is_available_for_dictation
    );

    const languageIsEnglish = outputLanguage.startsWith('en');

    return (
      languageIsEnglish || (isNonEnglishDictationEnabled && isLanguageDictationSupported)
    );
  }, [session, supportedLanguages, isNonEnglishDictationEnabled]);

  const shouldUseWordForWord = !isTabDictationEnabled || isAskAiSource;
  const dictationMode = shouldUseWordForWord
    ? DictationCleanupModes.WORD_FOR_WORD
    : (userDictationPreference ?? DictationCleanupModes.SMART);

  const handleStartDictation = useCallback(
    (dictationMode: DictationCleanupModes) => {
      editor?.commands.focus();
      const startedAt = new Date();
      setEditorDictateStatus((prev) => ({
        ...prev,
        tabName: source,
        startedAt,
      }));
      setLegacyDictateStatus((prev) => ({
        ...prev,
        tabName: source,
        startedAt,
      }));
      editor?.commands.startSpeechRecognition({
        shouldShowCursor: shouldShowCursor,
        dictationMode,
        sessionId: source === 'createTemplate' ? undefined : sessionId,
        editorId,
        onSuccess: onDictationSuccess,
      });
      if (
        source !== 'askAi' &&
        source !== 'createTemplate' &&
        userDictationPreference !== dictationMode
      ) {
        void updateSessionSettings({
          session_settings: {
            default_dictation_cleanup_mode: dictationMode,
          },
        });
      }
    },
    [
      editor,
      setEditorDictateStatus,
      setLegacyDictateStatus,
      shouldShowCursor,
      source,
      sessionId,
      userDictationPreference,
      updateSessionSettings,
      editorId,
      onDictationSuccess,
    ]
  );

  const handleStopDictation = useCallback(() => {
    setEditorDictateStatus((prev) => ({
      ...prev,
      isDictating: false,
      startedAt: null,
    }));
    setLegacyDictateStatus((prev) => ({
      ...prev,
      isDictating: false,
      startedAt: null,
    }));
    setShouldStartDictation(null);
    editor?.commands.stopSpeechRecognition();
  }, [editor, setEditorDictateStatus, setLegacyDictateStatus, setShouldStartDictation]);

  const startedAt = editorDictateStatus.startedAt;

  useEffect(() => {
    // TODO: fix this logic to be better
    // we use this to update state when command from the editor from / (slash command)
    if (
      editorDictateStatus.shouldInformTab &&
      editorDictateStatus.isDictating &&
      source !== 'askAi'
    ) {
      setEditorDictateStatus((prev) => ({
        ...prev,
        tabName: source,
        sessionId,
      }));
      setLegacyDictateStatus((prev) => ({
        ...prev,
        tabName: source,
        sessionId,
      }));
    }
  }, [
    editorDictateStatus.shouldInformTab,
    editorDictateStatus.isDictating,
    source,
    setEditorDictateStatus,
    setLegacyDictateStatus,
    sessionId,
  ]);

  return {
    isTabDictationEnabled,
    userDictationPreference,
    startDictatingTooltipText,
    isDictating,
    isLanguageSupported,
    isAskAiSource,
    isProcessing,
    dictationMode,
    handleStartDictation,
    handleStopDictation,
    startedAt,
  };
};
